import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Add as AddIcon, DeleteOutline as DeleteOutlineIcon, EditOutlined, PersonAddAlt1Outlined } from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { hasAnyPermission } from "../../config/permissionUtils";
import { useAuth } from "../../context/AuthContext";
import { customersService, groomingService, locationService } from "../../services";
import CustomerPetsDialog from "./CustomerPetsDialog";

const PHONE_MAX_LENGTH = 20;
const normalizePhoneValue = (value) => String(value || "").replace(/[^\d]/g, "").slice(0, PHONE_MAX_LENGTH);
const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const contactLabels = [
  { value: "PRIMARY", label: "Principal" },
  { value: "HOME", label: "Casa" },
  { value: "SPOUSE", label: "Esposa/o" },
  { value: "CHILD", label: "Hijo/a" },
  { value: "WORK", label: "Trabajo" },
  { value: "OTHER", label: "Otro" },
];

const createContactRow = (seed = {}) => ({
  local_id: seed.id || `local-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  id: seed.id || null,
  contact_name: seed.contact_name || "",
  relationship: seed.relationship || "",
  phone: normalizePhoneValue(seed.phone || ""),
  label: seed.label || "OTHER",
  is_primary: Boolean(seed.is_primary),
  is_active: seed.is_active !== false,
});

const extractErrorMessage = (error, fallback = "No se pudo guardar el cliente.") => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") {
    return fallback;
  }

  if (typeof data.detail === "string" && data.detail.trim()) {
    return data.detail;
  }

  const firstKey = Object.keys(data)[0];
  const firstValue = data[firstKey];
  if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
  if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  return fallback;
};

function SummaryRow({ label, value }) {
  return (
    <Box
      sx={(theme) => ({
        p: 1.35,
        borderRadius: 1.6,
        bgcolor: theme.palette.action.hover,
      })}
    >
      <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{label}</Typography>
      <Typography sx={{ mt: 0.35, fontSize: 13, fontWeight: 500 }}>
        {value || "-"}
      </Typography>
    </Box>
  );
}

function CustomerCreatePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { customerId } = useParams();
  const { currentBranch, effectivePerms = [], isSuperuser } = useAuth();
  const branchId = currentBranch?.id || null;
  const isEditMode = Boolean(customerId);
  const canViewPets = hasAnyPermission(
    effectivePerms,
    ["grooming_view", "grooming_manage", "grooming_convert_sale"],
    isSuperuser
  );

  const [form, setForm] = useState({
    name: "",
    nit: "",
    phone_number: "",
    email: "",
    address: "",
    city_id: "",
    department_id: "",
    is_active: true,
  });
  const [contacts, setContacts] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(isEditMode);
  const [loadingCities, setLoadingCities] = useState(true);
  const [loadingPets, setLoadingPets] = useState(false);
  const [customerPets, setCustomerPets] = useState([]);
  const [petsDialogOpen, setPetsDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const canSubmit = Boolean(form.name.trim()) && !submitting && !loadingInitial;

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  const loadDepartments = useCallback(async () => {
    try {
      const departments = await locationService.listDepartments({ active: "true" });
      setDepartmentOptions(toArray(departments));
    } catch {
      setDepartmentOptions([]);
    }
  }, []);

  const loadCities = useCallback(async (departmentId = "") => {
    setLoadingCities(true);
    try {
      const cities = await locationService.listCities({ active: "true", department_id: departmentId || undefined });
      setCityOptions(toArray(cities));
    } catch {
      setCityOptions([]);
    } finally {
      setLoadingCities(false);
    }
  }, []);

  const loadCustomer = useCallback(async () => {
    if (!isEditMode || !customerId) {
      setLoadingInitial(false);
      return;
    }
    setLoadingInitial(true);
    try {
      const customer = await customersService.get(customerId);
      const customerCity = customer?.city || {};
      const cityId = customerCity?.id ? String(customerCity.id) : "";
      const departmentId =
        customerCity?.department_id !== null && customerCity?.department_id !== undefined
          ? String(customerCity.department_id)
          : customerCity?.department?.id
            ? String(customerCity.department.id)
            : "";
      setForm({
        name: String(customer?.name || ""),
        nit: String(customer?.nit || ""),
        phone_number: normalizePhoneValue(customer?.phone_number || ""),
        email: String(customer?.email || ""),
        address: String(customer?.address || ""),
        city_id: cityId,
        department_id: departmentId,
        is_active: Boolean(customer?.is_active),
      });
      setContacts(toArray(customer?.contacts).map(createContactRow));
      if (departmentId) {
        loadCities(departmentId);
      } else {
        setCityOptions([]);
      }
    } catch (nextError) {
      const message = extractErrorMessage(nextError);
      setError(message);
      showSnack(message, "error");
    } finally {
      setLoadingInitial(false);
    }
  }, [customerId, isEditMode, loadCities, showSnack]);

  const loadPets = useCallback(async () => {
    if (!isEditMode || !customerId || !branchId || !canViewPets) {
      setCustomerPets([]);
      setLoadingPets(false);
      return;
    }
    setLoadingPets(true);
    try {
      const data = await groomingService.listCustomerPets({
        branch_id: branchId,
        customer_id: customerId,
      });
      setCustomerPets(Array.isArray(data?.pets) ? data.pets : []);
    } catch (nextError) {
      setCustomerPets([]);
      showSnack(extractErrorMessage(nextError, "No se pudieron cargar las mascotas del cliente."), "error");
    } finally {
      setLoadingPets(false);
    }
  }, [branchId, canViewPets, customerId, isEditMode, showSnack]);

  useEffect(() => {
    loadDepartments();
    if (!isEditMode) loadCities("");
    loadCustomer();
  }, [isEditMode, loadCities, loadCustomer, loadDepartments]);

  useEffect(() => {
    loadPets();
  }, [loadPets]);

  const handleChange = (field) => (event) => {
    const rawValue =
      event?.target?.type === "checkbox" ? event.target.checked : event.target.value;
    const value = field === "phone_number" ? normalizePhoneValue(rawValue) : rawValue;
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "department_id" ? { city_id: "" } : {}),
    }));
    if (field === "department_id") {
      loadCities(value);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError("");
    try {
      const payload = {
        name: form.name.trim(),
        nit: String(form.nit || "").trim() || undefined,
        phone_number: normalizePhoneValue(form.phone_number),
        email: String(form.email || "").trim() || undefined,
        address: String(form.address || "").trim() || undefined,
        city_id: form.city_id || undefined,
        is_active: Boolean(form.is_active),
        contacts: contacts
          .filter((contact) => normalizePhoneValue(contact.phone))
          .map((contact) => ({
            ...(contact.id ? { id: contact.id } : {}),
            phone: normalizePhoneValue(contact.phone),
            contact_name: String(contact.contact_name || "").trim(),
            relationship: String(contact.relationship || "").trim(),
            label: contact.label || "OTHER",
            is_primary: Boolean(contact.is_primary),
            is_active: Boolean(contact.is_active),
          })),
      };

      if (isEditMode) {
        await customersService.update(customerId, payload);
      } else {
        await customersService.create(payload);
      }

      navigate("/customers", {
        replace: true,
        state: {
          message: isEditMode ? "Cliente actualizado correctamente." : "Cliente creado correctamente.",
          severity: "success",
        },
      });
    } catch (nextError) {
      const message = extractErrorMessage(nextError);
      setError(message);
      showSnack(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedDepartmentLabel = useMemo(() => {
    const selected = departmentOptions.find((department) => String(department.id) === String(form.department_id));
    return selected?.name || "Sin departamento";
  }, [departmentOptions, form.department_id]);

  const selectedCityLabel = useMemo(() => {
    const selected = cityOptions.find((city) => String(city.id) === String(form.city_id));
    return selected?.name || "Sin ciudad";
  }, [cityOptions, form.city_id]);

  const addContact = () => {
    setContacts((prev) => [...prev, createContactRow()]);
  };

  const updateContact = (localId, field, rawValue) => {
    const value = field === "phone" ? normalizePhoneValue(rawValue) : rawValue;
    setContacts((prev) =>
      prev.map((contact) => {
        if (contact.local_id !== localId) return contact;
        return {
          ...contact,
          [field]: value,
          ...(field === "is_primary" && value ? { is_active: true } : {}),
        };
      }).map((contact) => (field === "is_primary" && value && contact.local_id !== localId ? { ...contact, is_primary: false } : contact))
    );
  };

  const removeContact = (localId) => {
    setContacts((prev) =>
      prev
        .map((contact) => (contact.local_id === localId ? { ...contact, is_active: false, is_primary: false } : contact))
        .filter((contact) => contact.id || contact.local_id !== localId)
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ "& .MuiTypography-h4, & .MuiTypography-h5": { fontWeight: 500 } }}>
        <MainText
          icon={isEditMode ? EditOutlined : PersonAddAlt1Outlined}
          title={isEditMode ? "Editar cliente" : "Nuevo cliente"}
          subtitle="Registro operativo de clientes sin datos tecnicos autogenerados"
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "420px minmax(0, 1fr)" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderLeft: `3px solid ${theme.palette.primary.main}`,
            borderRadius: "0 12px 12px 0",
          }}
        >
          <Stack spacing={1.5} component="form" onSubmit={handleSubmit}>
            <Typography sx={{ fontSize: 17, fontWeight: 500 }}>
              Datos del cliente
            </Typography>

            {error ? (
              <Alert severity="error" sx={{ fontSize: 12 }}>
                {error}
              </Alert>
            ) : null}

            <TextField
              label="Nombre completo *"
              size="small"
              value={form.name}
              onChange={handleChange("name")}
              fullWidth
              disabled={loadingInitial}
            />

            <TextField
              label="NIT"
              size="small"
              value={form.nit}
              onChange={handleChange("nit")}
              fullWidth
              disabled={loadingInitial}
              helperText="Si no tiene NIT, la venta puede seguir como CF."
            />

            <TextField
              label="Telefono"
              size="small"
              value={form.phone_number}
              onChange={handleChange("phone_number")}
              fullWidth
              disabled={loadingInitial}
              inputProps={{ maxLength: PHONE_MAX_LENGTH, inputMode: "numeric" }}
            />

            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.8, bgcolor: alpha(theme.palette.primary.main, 0.015) }}>
              <Stack spacing={1.2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                      Telefonos/contactos
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                      Agrega familiares o responsables sin duplicar el cliente.
                    </Typography>
                  </Box>
                  <Button size="small" startIcon={<AddIcon />} onClick={addContact} disabled={loadingInitial}>
                    Agregar
                  </Button>
                </Stack>
                {contacts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Sin telefonos adicionales. El telefono principal se guardara como contacto principal.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {contacts.map((contact) => (
                      <Paper
                        key={contact.local_id}
                        variant="outlined"
                        sx={{
                          p: 1,
                          borderRadius: 1.5,
                          opacity: contact.is_active ? 1 : 0.55,
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                            <TextField
                              label="Nombre contacto"
                              size="small"
                              value={contact.contact_name}
                              onChange={(event) => updateContact(contact.local_id, "contact_name", event.target.value)}
                              fullWidth
                              disabled={loadingInitial || !contact.is_active}
                            />
                            <TextField
                              label="Parentesco"
                              size="small"
                              value={contact.relationship}
                              onChange={(event) => updateContact(contact.local_id, "relationship", event.target.value)}
                              fullWidth
                              disabled={loadingInitial || !contact.is_active}
                            />
                            <TextField
                              label="Telefono"
                              size="small"
                              value={contact.phone}
                              onChange={(event) => updateContact(contact.local_id, "phone", event.target.value)}
                              inputProps={{ maxLength: PHONE_MAX_LENGTH, inputMode: "numeric" }}
                              fullWidth
                              disabled={loadingInitial || !contact.is_active}
                            />
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <TextField
                              select
                              label="Tipo"
                              size="small"
                              value={contact.label}
                              onChange={(event) => updateContact(contact.local_id, "label", event.target.value)}
                              disabled={loadingInitial || !contact.is_active}
                              sx={{ minWidth: 140 }}
                            >
                              {contactLabels.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={Boolean(contact.is_primary)}
                                  onChange={(event) => updateContact(contact.local_id, "is_primary", event.target.checked)}
                                  disabled={loadingInitial || !contact.is_active}
                                />
                              }
                              label="Principal"
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={Boolean(contact.is_active)}
                                  onChange={(event) => updateContact(contact.local_id, "is_active", event.target.checked)}
                                  disabled={loadingInitial}
                                />
                              }
                              label={contact.is_active ? "Activo" : "Inactivo"}
                            />
                            <IconButton size="small" color="error" onClick={() => removeContact(contact.local_id)} disabled={loadingInitial}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Paper>

            <TextField
              label="Correo"
              size="small"
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              fullWidth
              disabled={loadingInitial}
              helperText="Opcional. Solo se guarda si lo ingresas explicitamente."
            />

            <TextField
              select
              label="Departamento"
              size="small"
              value={form.department_id}
              onChange={handleChange("department_id")}
              fullWidth
              disabled={loadingInitial}
            >
              <MenuItem value="">Sin departamento</MenuItem>
              {departmentOptions.map((department) => (
                <MenuItem key={department.id} value={String(department.id)}>
                  {department.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Municipio/Ciudad"
              size="small"
              value={form.city_id}
              onChange={handleChange("city_id")}
              fullWidth
              disabled={loadingInitial || loadingCities || !form.department_id}
            >
              <MenuItem value="">Sin municipio</MenuItem>
              {cityOptions.map((city) => (
                <MenuItem key={city.id} value={String(city.id)}>
                  {city.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Direccion"
              size="small"
              multiline
              minRows={4}
              value={form.address}
              onChange={handleChange("address")}
              fullWidth
              disabled={loadingInitial}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(form.is_active)}
                  onChange={handleChange("is_active")}
                  disabled={loadingInitial}
                />
              }
              label={form.is_active ? "Cliente activo" : "Cliente inactivo"}
            />

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => navigate("/customers")} disabled={submitting}>
                Cancelar
              </Button>
              <Button type="submit" variant="contained" disabled={!canSubmit}>
                {submitting ? "Guardando..." : isEditMode ? "Actualizar cliente" : "Guardar cliente"}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Typography sx={{ fontSize: 17, fontWeight: 500 }}>
                Resumen
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  gap: 1.2,
                }}
              >
                <SummaryRow label="Cliente" value={form.name || "Sin nombre"} />
                <SummaryRow label="NIT" value={form.nit || "CF"} />
                <SummaryRow label="Telefono" value={form.phone_number || "Sin telefono"} />
                <SummaryRow label="Correo" value={form.email || "Sin correo"} />
                <SummaryRow label="Departamento" value={selectedDepartmentLabel} />
                <SummaryRow label="Ciudad" value={selectedCityLabel} />
                <SummaryRow label="Estado" value={form.is_active ? "Activo" : "Inactivo"} />
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.8,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.025),
                }}
              >
                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                  Direccion registrada
                </Typography>
                <Typography sx={{ mt: 0.35, fontSize: 13.5, fontWeight: 500, lineHeight: 1.6 }}>
                  {form.address || "Sin direccion por ahora"}
                </Typography>
              </Box>

              <Alert severity="info" sx={{ fontSize: 12 }}>
                El telefono se usa para identificar al cliente durante la venta. El nombre libre ya no crea clientes automaticamente.
              </Alert>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                <Box>
                  <Typography sx={{ fontSize: 17, fontWeight: 500 }}>
                    Mascotas
                  </Typography>
                  <Typography sx={{ mt: 0.35, fontSize: 13, color: "text.secondary" }}>
                    Resumen rapido de mascotas grooming del cliente en la sucursal actual.
                  </Typography>
                </Box>
                {isEditMode && canViewPets ? (
                  <Button
                    variant="outlined"
                    onClick={() => setPetsDialogOpen(true)}
                    disabled={!branchId}
                  >
                    Gestionar mascotas
                  </Button>
                ) : null}
              </Stack>

              {!isEditMode ? (
                <Alert severity="info" sx={{ fontSize: 12 }}>
                  Podras gestionar mascotas despues de guardar el cliente.
                </Alert>
              ) : !canViewPets ? (
                <Alert severity="warning" sx={{ fontSize: 12 }}>
                  No tienes permisos grooming para ver mascotas.
                </Alert>
              ) : !branchId ? (
                <Alert severity="info" sx={{ fontSize: 12 }}>
                  Selecciona una sucursal para gestionar mascotas de Grooming.
                </Alert>
              ) : loadingPets ? (
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                  Cargando mascotas...
                </Typography>
              ) : customerPets.length === 0 ? (
                <Alert severity="info" sx={{ fontSize: 12 }}>
                  Este cliente aun no tiene mascotas registradas en la sucursal actual.
                </Alert>
              ) : (
                <Stack spacing={1}>
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    {customerPets.length} mascota{customerPets.length === 1 ? "" : "s"} registrada{customerPets.length === 1 ? "" : "s"}
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {customerPets.slice(0, 6).map((pet) => (
                      <Chip
                        key={pet.id}
                        label={`${pet.name}${pet.breed ? ` - ${pet.breed}` : ""}`}
                        variant="outlined"
                      />
                    ))}
                    {customerPets.length > 6 ? (
                      <Chip label={`+${customerPets.length - 6} mas`} variant="outlined" />
                    ) : null}
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderLeft: `3px solid ${theme.palette.secondary.main}`,
              borderRadius: "0 12px 12px 0",
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
              Nota operativa
            </Typography>
            <Typography sx={{ mt: 0.7, fontSize: 13, color: "text.secondary", lineHeight: 1.7 }}>
              El correo es opcional y nunca se autocompleta con un valor tecnico. Si el cliente no tiene correo, el campo puede quedar vacio sin afectar la venta.
            </Typography>
          </Paper>
        </Stack>
      </Box>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />

      <CustomerPetsDialog
        open={petsDialogOpen}
        onClose={() => setPetsDialogOpen(false)}
        customer={isEditMode ? { id: Number(customerId), name: form.name, phone_number: form.phone_number } : null}
        onPetsChange={setCustomerPets}
      />
    </Box>
  );
}

export default CustomerCreatePage;
